<template>
  <div ref="submit" :class="['flex flex-col mx-auto w-full max-w-screen-2xl', layout]">
    <div v-if="avator || nickname" class="flex items-center mb-4">
      <div class="w-10 h-10 rounded-full overflow-hidden">
        <img v-bind="disalbeCopy" :src="avator" class="w-full h-full" />
      </div>
      <span class="ml-2 font-normal text">{{ nickname }}</span>
    </div>
    <template v-for="(item, index) in content.value">
      <div
        v-if="item.text"
        :key="index"
        :class="['prose prose-lg max-w-full lg:max-w-screen-2xl', textStyle(item.text)]"
        v-html="htmlText(item.text.content)"
      ></div>
      <div v-if="item.br === null" :key="index" class="h-4" />
      <div v-if="item.hr === null" :key="index" class="w-full py-4">
        <hr />
      </div>
      <div v-if="item.image" :key="index" :class="imageLayout(item.image)">
        <img
          v-bind="disalbeCopy"
          :src="webpImage(item.image.path)"
          :class="['max-w-full my-4', imageStyle(item.image)]"
        />
      </div>
      <div v-if="item.showSurvey" :key="index">
        <ReviewSurvey
          :content="{
            ...item.showSurvey,
            edit: 'disable'
          }"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import showdown from 'showdown';
import { webpImage } from '@forchange/cleword-utils';
import ReviewSurvey from '../review-survey/index.vue';
import { Content } from '@forchange/storytime';

import Html2canvas from 'html2canvas';
import { namespace } from 'vuex-class';
import { UserInfo } from '@/store/modules/common/types';
const commonModal = namespace('common');

@Component({
  components: {
    ReviewSurvey
  }
})
export default class Submit extends Vue {
  @Prop({ type: Object, default: () => {} }) content!: Content & { value: Array<any> };
  @commonModal.State('userInfo') userInfo: UserInfo;
  @commonModal.State('authingId') public authingId: string;

  get layout() {
    const values = this.content.value.filter((item) => {
      return !!item.layout;
    });
    if (!values[0]) return '';
    return this.markLayout(values[0].layout);
  }

  get avator() {
    return this.userInfo.avator;
  }
  get nickname() {
    return (
      this.userInfo.nickname ||
      `学员${
        (this.userInfo.phone && this.userInfo.phone.substring(this.userInfo.phone.length - 4)) ||
        (this.authingId && this.authingId.substring(this.authingId.length - 4))
      }`
    );
  }

  get disalbeCopy() {
    if (this.$store.state.common.disalbeCopy) {
      return {
        style:
          'user-select: none;-webkit-user-drag: none;-webkit-user-select: none;-webkit-touch-callout: none;',
        oncontextmenu: 'return false;'
      };
    }
    return {};
  }

  mounted() {
    this.isCourseFinished();
  }

  isCourseFinished() {
    if (
      this.content.value.some((item) => {
        return item.$kind === 'CourseFinished' && [true, '是'].includes(item.value);
      })
    ) {
      window.$tracker?.CourseFinishedTrack(this.content);
    }
  }

  webpImage(url: string) {
    return webpImage(url);
  }

  textStyle(text: { weight: number; size: number; align: string }) {
    const classArr = [];
    const fontWeight = [
      '',
      'font-thin',
      'font-extralight',
      'font-light',
      'font-normal',
      'font-medium',
      'font-semibold',
      'font-bold'
    ];
    const fontSize = [
      '',
      'text-xs',
      'text-sm',
      'text-base',
      'text-lg',
      'text-xl',
      'text-2xl',
      'text-3xl'
    ];

    if (text.weight) {
      classArr.push(fontWeight[text.weight]);
    }

    if (text.size) {
      classArr.push(fontSize[text.size]);
    }

    switch (text.align) {
      case '居左':
      case 'left':
        classArr.push('self-start');
        break;
      case '居中':
      case 'center':
        classArr.push('self-center');
        break;
      case '居右':
      case 'right':
        classArr.push('self-end');
        break;
      default:
        classArr.push('self-start');
    }

    return classArr.join(' ');
  }

  imageLayout(image: { align: string }) {
    const classArr = [];
    switch (image.align) {
      case '居左':
      case 'left':
        classArr.push('self-start');
        break;
      case '居中':
      case 'center':
        classArr.push('self-center');
        break;
      case '居右':
      case 'right':
        classArr.push('self-end');
        break;
      default:
        classArr.push('self-start');
    }
    return classArr.join(' ');
  }

  imageStyle(image: { size: number }) {
    const fontSize = ['', 'w-16', 'w-32', 'w-64', 'w-96', 'w-128', 'w-160', 'w-200'];
    return fontSize[image.size] || '';
  }

  markLayout(layout: { xAxis: string; yAxis: string; width: string }) {
    const classArr = [];

    switch (layout.xAxis) {
      case '居左':
      case 'left':
        classArr.push('items-start');
        break;
      case '居中':
      case 'center':
        classArr.push('items-center');
        break;
      case '居右':
      case 'right':
        classArr.push('items-end');
        break;
      default:
        classArr.push('items-start');
    }

    switch (layout.yAxis) {
      case '顶部':
      case 'top':
        classArr.push('justify-start');
        break;
      case '居中':
      case 'center':
        classArr.push('justify-center');
        break;
      case '底部':
      case 'bottom':
        classArr.push('justify-end');
        break;
      default:
        classArr.push('justify-start');
    }

    // width
    switch (layout.width) {
      case '大':
      case 'large':
        classArr.push('lg:w-11/12');
        break;
      case '中':
      case 'middle':
        classArr.push('lg:w-8/12');
        break;
      case '小':
      case 'small':
        classArr.push('lg:w-6/12');
        break;
      default:
        classArr.push('lg:w-10/12');
    }

    return classArr.join(' ');
  }

  htmlText(value: string): string {
    const converter = new showdown.Converter({
      simpleLineBreaks: true,
      openLinksInNewWindow: true
    });
    return converter.makeHtml(value);
  }

  async screenshot() {
    const canvasSourceEl = this.cloneElement(this.$refs.submit as HTMLDivElement);
    if (!canvasSourceEl?.cloneEl) return;
    canvasSourceEl.cloneEl.setAttribute('class', 'md:p-20 p-4');
    await this.imageLocal(canvasSourceEl.cloneEl);
    this.replaceTextear(canvasSourceEl.cloneEl);
    const canvasEl = await Html2canvas(canvasSourceEl.cloneEl);
    canvasSourceEl.removeHook();
    return canvasEl.toDataURL('image/png');
  }

  replaceTextear(canvasSourceEl: HTMLDivElement) {
    // 规避html2canvas textarea渲染问题
    const textareaEls = canvasSourceEl.querySelectorAll('.textarea');
    textareaEls.forEach((textareaEl) => {
      const divEl = document.createElement('div');
      divEl.setAttribute('style', 'padding: 1rem; white-space: pre-line; line-height: 1.75rem;');
      divEl.innerHTML = (textareaEl as HTMLTextAreaElement).value;
      textareaEl.parentElement?.replaceChild(divEl, textareaEl);
    });
  }

  cloneElement(sourceEl: HTMLDivElement) {
    if (!sourceEl) return;
    const el = sourceEl.cloneNode(true);
    const container = document.createElement('div');
    container.appendChild(el);
    container.setAttribute('id', 'canvas-view');
    document.body.appendChild(container);
    const cloneEl = document.body.querySelector('#canvas-view') as HTMLDivElement;
    return {
      cloneEl,
      removeHook: () => {
        document.body.removeChild(container);
      }
    };
  }

  // 图片本地化
  async imageLocal(sourceEl: HTMLDivElement) {
    const imgEls = sourceEl.querySelectorAll('img');
    for (const imgEl of imgEls) {
      const imageUrl = imgEl.getAttribute('src');
      if (!imageUrl?.startsWith('http') || !imageUrl) continue;
      let url = '';
      try {
        url = await new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = document.createElement('img');
          img.setAttribute('crossorigin', 'anonymous');

          img.src = imageUrl;
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL());
          };
          img.onerror = (e) => {
            reject(e);
          };
        });
      } catch (error) {
        console.log('error: ', error);
      }
      if (url) {
        imgEl.setAttribute('src', url);
      }
    }
  }
}
</script>
<style scoped>
.image-max {
  max-height: 80vh;
}
.survey {
  @apply w-full mt-8;
}
</style>
